import { useLayoutEffect, useRef } from 'react';
// Utils
import cn from '../../../../utils/cn';
// Styles
import styles from './TextareaCell.module.css';
import commonStyles from '../TableCellCommon.module.css';

/**
 * TextareaCell component for displaying multi-line text in table cells (read-only)
 * @param {object} props - Component props
 * @param {string} [props.value=''] - The text value to display
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.placeholder='-'] - Placeholder text when empty
 * @param {Align} [props.align='left'] - Text alignment
 * @param {VerticalAlign} [props.verticalAlign='middle'] - Vertical alignment
 * @param {boolean} [props.bold=false] - Text in bold
 * @param {boolean} [props.italic=false] - Text in italic
 * @param {boolean} [props.strikeThrough=false] - Text with strike-through
 * @param {boolean} [props.underline=false] - Text underlined
 * @returns {JSX.Element} TextareaCell component
 */
export default function TextareaCell({
  value = '',
  className = '',
  placeholder = '-',
  align = 'left',
  verticalAlign = 'middle',
  bold = false,
  italic = false,
  strikeThrough = false,
  underline = false,
}) {
  const isEmpty = !value || value === '';

  // The ref here is used to sync the textarea height with the content (because if the user reduces the column width, the textarea height will not be adjusted). We need to watch the height of the content to adjust the height of the textArea and the height of the cell.
  const textareaRef = useRef(/** @type {HTMLTextAreaElement | null} */ (null));

  const syncTextareaHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  // calculate height if value changed
  useLayoutEffect(() => {
    syncTextareaHeight();
  }, [value, isEmpty]);

  // calculate height on column resize
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      syncTextareaHeight();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, []);

  // `data-table-cell-layout` lets DirectEditCommon give this cell full column width (vs fit-content slot).
  return (
    <div
      data-table-cell-layout="fill"
      className={cn([
        styles['textarea-cell-wrapper'],
        className,
        bold && commonStyles['cell-bold'],
        italic && commonStyles['cell-italic'],
        strikeThrough && commonStyles['cell-strike-through'],
        underline && commonStyles['cell-underline'],
        align && commonStyles[`cell-align-${align}`],
        verticalAlign && commonStyles[`cell-vertical-align-${verticalAlign}`],
      ])}
    >
      <textarea
        ref={textareaRef}
        className={cn([styles['textarea-cell-input']])}
        value={isEmpty ? '' : value}
        placeholder={isEmpty ? placeholder : undefined}
        readOnly
        rows={1}
      />
    </div>
  );
}
